import * as styles from "./ResultsList.css";

import { type ReactNode, useCallback, useEffect, useRef } from "react";

import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useVirtualizer } from "@tanstack/react-virtual";

export type ResultsListProps<T> = {
  items: T[];
  itemKey: (item: T) => string;
  highlightedIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (item: T, index: number) => void;
  estimateSize: (index: number) => number;
  children: (item: T, index: number, highlighted: boolean) => ReactNode;
};

export function ResultsList<T>({
  items,
  itemKey,
  highlightedIndex,
  onHighlight,
  onSelect,
  estimateSize,
  children,
}: ResultsListProps<T>) {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize,
    overscan: 8,
  });

  const handleViewportRef = useCallback((element: HTMLDivElement | null) => {
    scrollElementRef.current = element;
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && highlightedIndex < items.length) {
      virtualizer.scrollToIndex(highlightedIndex, { align: "auto" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedIndex]);

  const totalSize = virtualizer.getTotalSize();

  return (
    <ScrollArea className={styles.scrollArea}>
      <ScrollAreaViewport ref={handleViewportRef} className={styles.viewport}>
        <div
          role="listbox"
          className={styles.listInner}
          style={{ height: totalSize }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = items[virtualItem.index];

            if (!item) {
              return null;
            }

            const highlighted = virtualItem.index === highlightedIndex;

            return (
              <div
                key={itemKey(item)}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                role="option"
                aria-selected={highlighted}
                data-highlighted={highlighted ? "" : undefined}
                className={styles.item}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                onMouseEnter={() => onHighlight(virtualItem.index)}
                onClick={() => onSelect(item, virtualItem.index)}
              >
                {children(item, virtualItem.index, highlighted)}
              </div>
            );
          })}
        </div>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar>
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
    </ScrollArea>
  );
}
